import { useAuthStore, type AuthUser, type UserRole } from '@/stores/authStore';
import { useSimulationStore } from '@/stores/simulationStore';

export function resolveEffectiveRole(user: AuthUser | null, isSimulating: boolean): UserRole | null {
  if (!user) return null;
  return isSimulating ? 'customer' : user.role;
}

/**
 * The rights of the session, seen through the simulation.
 *
 * Story 4.1 / AD-15. `resolveEffectiveRole` turns the role into `customer` while an administrator
 * looks at the portal as a client, but it leaves `user.permissions` untouched — the rights travel
 * in the access token and nothing rewrites them. Anything reading the rights straight from the
 * store therefore still sees them, and would keep an internal menu item or an internal screen
 * open during a simulation. This is the one place that corrects it, so the sidebar and the route
 * guard do not each need their own version of the same rule.
 *
 * Note what `useEffectiveAccess().permissions` therefore means: the rights that apply *right now*,
 * not the rights the session carries — the same shift `role` already makes. AD-15 words the sidebar
 * rule as "`!isSimulating` **and** the rights intersect"; folding the first half in here keeps the
 * two halves from drifting apart and makes every future reader inherit the rule rather than have
 * to remember it. Whoever needs the raw session rights reads `user.permissions` and says why.
 */
export function resolveEffectivePermissions(user: AuthUser | null, isSimulating: boolean): readonly string[] {
  if (!user || isSimulating) return [];
  // Een kopie, niet de array uit de store: wie hem sorteert of aanvult zou anders de rechten van de
  // sessie wijzigen voor elke andere lezer.
  return Array.isArray(user.permissions) ? [...user.permissions] : [];
}

export function resolveEffectiveClientId(routeClientId: string | undefined, user: AuthUser | null, isSimulating: boolean, simulatedGln: string | null): string {
  if (isSimulating && simulatedGln) return simulatedGln;
  // Story fu-5 (LIVE BLOCKER): `glnNumbers` must be array-indexed defensively — a malformed
  // or partial auth-user payload (the exact ACC crash) must degrade to '' instead of throwing.
  return routeClientId ?? user?.glnNumbers?.[0] ?? '';
}

export function useEffectiveAccess() {
  const user = useAuthStore((state) => state.user);
  const isSimulating = useSimulationStore((state) => state.isSimulating);
  const simulatedGln = useSimulationStore((state) => state.simulatedGln);
  return {
    user,
    isSimulating,
    simulatedGln,
    role: resolveEffectiveRole(user, isSimulating),
    permissions: resolveEffectivePermissions(user, isSimulating),
  };
}
